import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Field, FormError } from "./Common";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const EMPTY = { name: "", label: "", result_item: "", result_amount: 1, craft_time_seconds: 10, materials: [{ item: "", amount: 1 }], allowed_org_ids: [], zone_ids: [], active: true };

const toForm = (r) => (r?.id ? { ...EMPTY, ...r, materials: r.materials.map((m) => ({ ...m })) } : { ...EMPTY, materials: [{ item: "", amount: 1 }] });

const MAX_AMOUNT = 2147483647;
const isInt = (v, min) => /^\d+$/.test(String(v)) && Number(v) >= min && Number(v) <= MAX_AMOUNT;

const validate = (f) => {
  if (!f.name.trim() || !f.label.trim() || !f.result_item.trim()) return "Nome, nome visível e item resultante são obrigatórios.";
  if (!isInt(f.result_amount, 1)) return "Quantidade resultante: número inteiro ≥ 1";
  if (!isInt(f.craft_time_seconds, 1) || Number(f.craft_time_seconds) > 3600) return "Tempo de craft: entre 1 e 3600 segundos";
  if (f.materials.length === 0) return "Adiciona pelo menos 1 material.";
  if (f.materials.some((m) => !m.item.trim() || !isInt(m.amount, 1))) return "Cada material precisa de item e quantidade ≥ 1.";
  return "";
};

const Chips = ({ title, hint, items, selected, onToggle, prefix }) => (
  <div>
    <div className="eyebrow mb-2 text-slate-400">{title}</div>
    <div className="flex flex-wrap gap-2" data-testid={`${prefix}-multiselect`}>
      {items.map((it) => {
        const on = selected.includes(it.id);
        return (
          <button key={it.id} type="button" onClick={() => onToggle(it.id)} data-testid={`${prefix}-option-${it.key}`}
            className={`flex items-center gap-2 border px-3 py-1.5 text-xs transition-colors duration-150 ${on ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}>
            {it.color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: it.color }} />}{it.label}
          </button>
        );
      })}
    </div>
    <p className="mt-1.5 font-mono text-[11px] text-slate-500">{selected.length === 0 ? hint : `${selected.length} selecionada(s)`}</p>
  </div>
);

const MaterialRows = ({ materials, onChange }) => {
  const set = (i, k, v) => onChange(materials.map((m, j) => (j === i ? { ...m, [k]: v } : m)));
  return (
    <div>
      <div className="eyebrow mb-2 text-slate-400">Materiais (por unidade)</div>
      <div className="space-y-2">
        {materials.map((m, i) => (
          <div key={i} className="flex items-end gap-2" data-testid={`recipe-material-row-${i}`}>
            <Field label="Item" value={m.item} onChange={(e) => set(i, "item", e.target.value)} testid={`recipe-material-item-${i}`} placeholder="ex: metalscrap" className="flex-1" />
            <Field label="Qtd." type="number" min="1" value={m.amount} onChange={(e) => set(i, "amount", e.target.value)} testid={`recipe-material-amount-${i}`} className="w-24" />
            <Button type="button" size="icon" variant="ghost" onClick={() => onChange(materials.filter((_, j) => j !== i))} disabled={materials.length === 1} data-testid={`recipe-material-remove-${i}`}>
              <Trash2 className="h-4 w-4 text-rose-400" />
            </Button>
          </div>
        ))}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...materials, { item: "", amount: 1 }])} className="mt-2 border-slate-700 bg-transparent" data-testid="recipe-material-add">
        <Plus className="mr-1 h-3.5 w-3.5" />Adicionar material
      </Button>
    </div>
  );
};

export const RecipeDialog = ({ recipe, orgs, zones, onClose, onSaved }) => {
  const isNew = !recipe?.id;
  const [form, setForm] = useState(toForm(null));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(toForm(recipe)); setError(""); }, [recipe]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => (id) => setForm({ ...form, [k]: form[k].includes(id) ? form[k].filter((x) => x !== id) : [...form[k], id] });

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate(form);
    if (msg) return setError(msg);
    const payload = {
      ...form, result_amount: Number(form.result_amount), craft_time_seconds: Number(form.craft_time_seconds),
      materials: form.materials.map((m) => ({ item: m.item.trim(), amount: Number(m.amount) })),
    };
    delete payload.id;
    delete payload.created_at;
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/recipes", payload);
      else await api.put(`/admin/recipes/${recipe.id}`, payload);
      toast.success(isNew ? "Receita criada." : "Receita atualizada.");
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Erro ao guardar receita."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!recipe} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-950" data-testid="recipe-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">{isNew ? "Nova receita" : "Editar receita"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nome (identificador)" value={form.name} onChange={set("name")} testid="recipe-name-input" placeholder="ex: pistola" />
            <Field label="Nome visível" value={form.label} onChange={set("label")} testid="recipe-label-input" placeholder="ex: Pistola" />
          </div>
          <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
            <Field label="Item resultante" value={form.result_item} onChange={set("result_item")} testid="recipe-result-item-input" placeholder="ex: weapon_pistol" />
            <Field label="Qtd. resultante" type="number" min="1" value={form.result_amount} onChange={set("result_amount")} testid="recipe-result-amount-input" />
            <Field label="Tempo (s)" type="number" min="1" value={form.craft_time_seconds} onChange={set("craft_time_seconds")} testid="recipe-time-input" />
          </div>
          <MaterialRows materials={form.materials} onChange={(materials) => setForm({ ...form, materials })} />
          <Chips title="Organizações permitidas" hint="Nenhuma selecionada = qualquer organização que domine a zona" prefix="recipe-org" selected={form.allowed_org_ids} onToggle={toggle("allowed_org_ids")}
            items={(orgs || []).map((o) => ({ id: o.id, key: o.name, label: o.label, color: o.color }))} />
          <Chips title="Zonas" hint="Nenhuma selecionada = todas as zonas" prefix="recipe-zone" selected={form.zone_ids} onToggle={toggle("zone_ids")}
            items={(zones || []).map((z) => ({ id: z.id, key: z.id, label: z.name }))} />
          <label className="flex h-10 w-fit items-center gap-3 border border-slate-800 px-3">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="recipe-active-switch" />
            <span className="text-sm">{form.active ? "Ativa" : "Inativa"}</span>
          </label>
          <FormError message={error} testid="recipe-form-error" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 bg-transparent" data-testid="recipe-cancel-button">Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="recipe-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
